// ---------------------------------------------------------------------------
// portalQueue — durable, offline-tolerant write queue for the crew portal.
// Phase 1 (writes-only). See
// BBM Context for claude/crew-portal-coreloop-offline-design-v1.md.
//
// What it does:
//   • Persists pending POSTs to /api/crew-portal-data in IndexedDB so a save
//     made with no signal survives app close / reload and syncs later.
//   • Dedupes by `key` (put() replaces same-key), so re-saving the same job
//     recap / day hours / truck photo collapses to ONE pending item — no
//     duplicate uploads, no redundant syncs.
//   • Flushes on reconnect / focus / visibility / a light interval.
//   • Exposes a snapshot + subscribe() so the UI can show
//     saved · syncing · synced · needs-attention and an end-of-day roll-up.
//
// Degrades gracefully: if IndexedDB is unavailable (private mode, old WebKit),
// enqueueAndSync() falls back to a plain network POST — i.e. exactly today's
// behavior, never worse.
//
// Scope: the three end-of-day saves (submit_recap, save_day_hours,
// save_day_truck_photo). add_waiver stays a live online flow (it opens the
// signing tab on success), so it's intentionally not queued here.
// ---------------------------------------------------------------------------

const DB_NAME = 'bbm_crew_portal';
const STORE = 'write_queue';
const DB_VERSION = 1;
const ENDPOINT = '/api/crew-portal-data';
const MAX_ATTEMPTS = 8;

let _authHeadersFn = async () => ({});
let _flushing = false;
let _initialized = false;
let _snapshot = [];
const _listeners = new Set();

// ── IndexedDB plumbing (dependency-free) ────────────────────────────────
function idbAvailable() {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'key' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function reqP(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

async function getAllItems() {
    if (!idbAvailable()) return [];
    const db = await openDB();
    try {
        return (await reqP(db.transaction(STORE).objectStore(STORE).getAll())) || [];
    } finally {
        db.close();
    }
}

async function putItem(item) {
    const db = await openDB();
    try {
        await reqP(db.transaction(STORE, 'readwrite').objectStore(STORE).put(item));
    } finally {
        db.close();
    }
}

async function deleteItem(key) {
    const db = await openDB();
    try {
        await reqP(db.transaction(STORE, 'readwrite').objectStore(STORE).delete(key));
    } finally {
        db.close();
    }
}

// ── Snapshot + subscription ─────────────────────────────────────────────
async function refreshSnapshot() {
    try {
        _snapshot = await getAllItems();
    } catch {
        /* keep last snapshot */
    }
    for (const fn of _listeners) {
        try { fn(_snapshot); } catch { /* ignore listener error */ }
    }
}

export function getSnapshot() {
    return _snapshot;
}

export function subscribe(fn) {
    _listeners.add(fn);
    fn(_snapshot);
    return () => _listeners.delete(fn);
}

// Roll-up counts for the UI sync chip.
export function queueStats(snapshot = _snapshot) {
    let pending = 0, failed = 0;
    for (const it of snapshot) {
        if (it.status === 'failed') failed++;
        else pending++;
    }
    return { pending, failed, total: snapshot.length };
}

// Is there a pending/failed item for this key? (drives optimistic ✓ marks)
export function hasPending(key, snapshot = _snapshot) {
    return snapshot.some(it => it.key === key);
}

function isOffline() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// ── Core: post one item, transition its state ───────────────────────────
// Returns { outcome: 'synced'|'offline'|'failed'|'server_error', body?, error? }
async function _syncOne(item) {
    try {
        await putItem({ ...item, status: 'syncing', updatedAt: Date.now() });
        await refreshSnapshot();
    } catch { /* IDB hiccup — proceed to attempt anyway */ }

    let res;
    try {
        const headers = { 'Content-Type': 'application/json', ...(await _authHeadersFn()) };
        res = await fetch(ENDPOINT, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify(item.payload),
        });
    } catch (e) {
        // Network failure — keep it pending, we're offline.
        await safePut({ ...item, status: 'pending', attempts: item.attempts + 1, lastError: 'offline', updatedAt: Date.now() });
        return { outcome: 'offline' };
    }

    if (res.ok) {
        let body = null;
        try { body = await res.json(); } catch { /* empty body ok */ }
        try { await deleteItem(item.key); } catch { /* ignore */ }
        await refreshSnapshot();
        return { outcome: 'synced', body };
    }

    if (res.status >= 400 && res.status < 500) {
        // Validation/auth — won't fix itself on retry. Flag for attention.
        let msg = `Server returned ${res.status}`;
        try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
        await safePut({ ...item, status: 'failed', attempts: item.attempts + 1, lastError: msg, updatedAt: Date.now() });
        return { outcome: 'failed', error: msg };
    }

    // 5xx — transient, keep pending for the next flush.
    await safePut({ ...item, status: 'pending', attempts: item.attempts + 1, lastError: `Server ${res.status}`, updatedAt: Date.now() });
    return { outcome: 'server_error', error: `Server ${res.status}` };
}

async function safePut(item) {
    try { await putItem(item); await refreshSnapshot(); } catch { /* ignore */ }
}

// ── Public: enqueue + immediate attempt (used by save buttons) ──────────
// item: { key, action, payload, label }. payload is the full POST body
// (it already carries action + token). Returns the _syncOne outcome, or a
// direct-POST result when IndexedDB isn't available.
export async function enqueueAndSync({ key, action, payload, label }) {
    const now = Date.now();
    const item = { key, action, payload, label: label || action, status: 'pending', attempts: 0, lastError: null, createdAt: now, updatedAt: now };

    if (!idbAvailable()) {
        // No durable store — behave like a normal online POST (today's behavior).
        return _directPost(item);
    }

    try {
        await putItem(item); // replaces any existing item with the same key (dedupe)
        await refreshSnapshot();
    } catch {
        return _directPost(item);
    }

    if (isOffline()) return { outcome: 'offline' };
    return _syncOne(item);
}

async function _directPost(item) {
    try {
        const headers = { 'Content-Type': 'application/json', ...(await _authHeadersFn()) };
        const res = await fetch(ENDPOINT, { method: 'POST', credentials: 'include', headers, body: JSON.stringify(item.payload) });
        if (res.ok) { let body = null; try { body = await res.json(); } catch { /* ignore */ } return { outcome: 'synced', body }; }
        let msg = `Server returned ${res.status}`;
        try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
        return { outcome: res.status >= 400 && res.status < 500 ? 'failed' : 'server_error', error: msg };
    } catch {
        return { outcome: 'offline' };
    }
}

// ── Flush the whole queue (auto-triggered) ──────────────────────────────
export async function flush() {
    if (_flushing || isOffline() || !idbAvailable()) return;
    _flushing = true;
    try {
        const items = (await getAllItems()).sort((a, b) => a.createdAt - b.createdAt);
        for (const item of items) {
            if (item.status === 'failed' && item.attempts >= MAX_ATTEMPTS) continue;
            const r = await _syncOne(item);
            if (r.outcome === 'offline') break; // lost signal — stop, try again later
        }
    } catch {
        /* ignore — next trigger retries */
    } finally {
        _flushing = false;
    }
}

// Manually retry the failed items (e.g. from a "retry" button).
export async function retryFailed() {
    if (!idbAvailable()) return;
    try {
        const items = await getAllItems();
        for (const it of items) {
            if (it.status === 'failed') await safePut({ ...it, status: 'pending', attempts: 0, lastError: null });
        }
    } catch { /* ignore */ }
    flush();
}

// ── Init: wire auth + auto-flush triggers (call once on portal mount) ───
export function initSync(getAuthHeadersFn) {
    if (typeof getAuthHeadersFn === 'function') _authHeadersFn = getAuthHeadersFn;
    refreshSnapshot();
    if (_initialized) { flush(); return; }
    _initialized = true;
    if (typeof window !== 'undefined') {
        window.addEventListener('online', () => flush());
        window.addEventListener('focus', () => flush());
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => { if (!document.hidden) flush(); });
        }
        setInterval(() => { if (_snapshot.length) flush(); }, 30000);
    }
    flush();
}
